import { Component, inject, computed, input, Signal } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { RIGHT_PANEL_CONFIG } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { RightPanelService } from "@bitwarden/web-vault/app/components/dialog/right-panel.service";
import {
  EditTransactionDetailsComponent,
  editTransactionTab,
} from "@vault/web-vault/app/gloss/manage/manage-accounts/edit-transaction-details/edit-transaction-details.component";
import { TableRow } from "@vault/web-vault/app/components/tables/transaction-table/transaction-table";
import { TransactionOrigin } from "@vault/web-vault/app/models/types/transaction.types";
import { I18nService } from "@vault/common/abstractions/i18n.service";
import { getTransactionOrigin } from "@vault/web-vault/app/shared/utils/helper.transactions/transaction-origin";
import { DeleteItem, deletingItem } from "@vault/web-vault/app/components/tables/deleteItem";
import { TransactionDirection } from "@vault/web-vault/app/models/enum/transactionDirection";
import { UserStoreService } from "@vault/web-vault/app/services/store/user/user.store.service";
import { TransactionStoreService } from "@vault/web-vault/app/services/store/transaction/transaction.store.service";
import { CategoryView } from "@vault/web-vault/app/models/view/category/category.view";
import { InstitutionOriginEnum } from "@vault/web-vault/app/models/types/institution.type";
import { TransactionView } from "@vault/web-vault/app/models/view/transaction/transaction.view";
import { AccountView } from "@vault/web-vault/app/models/view/account/account.view";
import { GlossNumber } from "@vault/web-vault/app/models/data/shared/gloss-number";
import { RoleAccessService } from "@vault/web-vault/app/services/permission/role-access.service";
import { TransactionLinkService } from "@vault/web-vault/app/services/DataService/transaction/transaction-link.service";
import { LogService } from "@vault/common/abstractions/log.service";
import { PlatformUtilsService } from "@vault/common/abstractions/platformUtils.service";
import { ConfirmationDialogService } from "@vault/web-vault/app/services/confirmation/confirmation.service";
import { ConfirmationEnum } from "@vault/web-vault/app/models/enum/confirmation.enum";
import { LinkTransactionPickerComponent } from "@vault/web-vault/app/gloss/manage/manage-accounts/transaction-details/link-transaction-picker.component";

export interface TransactionData {
  amount: number;
  origin: string;
  date: string;
  description: string;
  direction: TransactionDirection;
  source: string;
  symbol: string;
  account: string;
  accounts: Array<object>;
  categoriesIds: string[];
  transactionSymbol: GlossNumber;
}

export interface StockTransactionData extends TransactionData {
  totalValue: number;
  unitPrice: number;
  baseCurrencySymbol: string;
}

export interface TransactionDetailType {
  transactionView: TransactionView;
  accountView: AccountView;
  transactionDate: string;
  description: string;
  institution: string;
  account: string;
  source: string;
}

const stockOtherAccountType = ["other", "stock"];

@Component({
  templateUrl: "./transaction-details.component.html",
})
export class TransactionDetailsComponent {
  private readonly rightPanel = inject(RightPanelService);
  rowData = input(null);
  protected i18nService: I18nService = inject(I18nService);
  private deleteItem = new DeleteItem(deletingItem.transaction);
  private userStoreService: UserStoreService = inject(UserStoreService);
  private transactionStoreService: TransactionStoreService = inject(TransactionStoreService);
  private roleAccessService: RoleAccessService = inject(RoleAccessService);
  private transactionLinkService: TransactionLinkService = inject(TransactionLinkService);
  private platformUtilsService: PlatformUtilsService = inject(PlatformUtilsService);
  private logService: LogService = inject(LogService);
  private confirmationDialogService: ConfirmationDialogService = inject(ConfirmationDialogService);
  protected InstitutionOriginEnum = InstitutionOriginEnum;
  protected TransactionDirection = TransactionDirection;
  readonly origins: Record<string, TransactionOrigin> = {
    manual: "manual",
    basiq: "basiq",
    plaid: "plaid",
    fiskil: "fiskil",
  };

  private readonly originImageExtensions: Partial<Record<TransactionOrigin, string>> = {
    basiq: "png",
    plaid: "png",
    fiskil: "svg",
  };

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<TransactionDetailsComponent>,
  ) {}

  protected isStockOrOtherAccount = computed(() => {
    // accountView can be undefined for a transaction whose account no longer exists (e.g. opened
    // directly from a search result for a deleted account) — treat that as "not stock/other"
    // rather than throwing, matching the optional accountView handling already used in getData().
    return stockOtherAccountType.includes(this.rowData().accountView?.type);
  });

  /** The latest enriched view for this transaction, falling back to the row's
      snapshot. Reactive to the store, so link state stays current after an
      unlink. */
  protected currentTransaction = computed<TransactionView>(() => {
    const rowData = this.rowData();
    const latestTransaction = (
      this.transactionStoreService.enrichTransaction.collection() ?? []
    ).find((t) => t.id === rowData?.transactionView?.id);
    return latestTransaction ?? rowData?.transactionView;
  });

  protected data: Signal<TransactionData | StockTransactionData> = computed(() => {
    return this.getData({
      ...this.rowData(),
      transactionView: this.currentTransaction(),
    });
  });

  /** Transfer/conversion link state for the manual unlink control (beta only). */
  protected linkInfo = computed(() => {
    const transaction = this.currentTransaction();
    if (!this.roleAccessService.isBetaUserSignal() || !transaction) {
      return { isLinked: false, linkType: null };
    }
    const isLinked =
      (transaction.linkedTo?.length ?? 0) > 0 || (transaction.c_linkedTo?.length ?? 0) > 0;
    return { isLinked, linkType: transaction.c_linkType };
  });

  /** Whether to show the manual "link to another transaction" entry point: beta
      user, transaction resolved, and not already linked. */
  protected canManualLink = computed(() => {
    return (
      this.roleAccessService.isBetaUserSignal() &&
      !!this.currentTransaction() &&
      !this.linkInfo().isLinked
    );
  });

  protected stockOtherData = computed(() => {
    const data = this.data();
    return this.isStockTransactionData(data) ? data : null;
  });

  protected displayAmount = computed(() => {
    const stockData = this.stockOtherData();
    const raw =
      this.isStockOrOtherAccount() && stockData
        ? stockData.totalValue
        : this.data().transactionSymbol.amount;
    return Math.abs(raw);
  });

  protected categories = computed(() => {
    const categoriesFromUser = this.userStoreService.categories.categoryViewsSorted();
    const categories: CategoryView[] = [];
    this.data().categoriesIds.forEach((categoryId) => {
      categories.push(categoriesFromUser.find((category) => category.id === categoryId));
    });
    if (!categories[0]) {
      return [];
    }
    return categories;
  });

  protected origin = computed(() => {
    return this.origins[this.data().origin];
  });

  protected isManual = computed(() => this.origin() === this.origins.manual);

  protected originImageExtension = computed(() => this.originImageExtensions[this.origin()]);

  async delete(): Promise<void> {
    this.deleteItem
      .delete(this.rowData().transactionView)
      .then((res) => res && this.dialogRef?.close());
  }

  /** Manually break this transaction's transfer/conversion link. Confirmed first
      (consistent with `delete()`): unlinking durably excludes the pair from
      auto-detection (`xLid`), so it reads as irreversible until the manual link
      picker lands. The store refresh re-drives `linkInfo`, so the control hides
      once unlinked. A failed vault write is caught and surfaced so the badge
      doesn't silently stay put; a transaction deleted elsewhere returns `false`
      (nothing to unlink), so close the now-stale panel rather than no-op. */
  async unlink(): Promise<void> {
    const confirmed = await this.confirmationDialogService.confirmFor(
      ConfirmationEnum.unlinkingTransaction,
    );
    if (!confirmed) {
      return;
    }
    try {
      const unlinked = await this.transactionLinkService.unlink(this.currentTransaction());
      if (!unlinked) {
        // The transaction was deleted elsewhere while this dialog stayed open, so
        // there is nothing to unlink and the panel is showing a stale view —
        // close it (consistent with delete()) instead of silently doing nothing.
        this.dialogRef?.close();
      }
    } catch (e) {
      this.logService.error(e instanceof Error ? (e.stack ?? e.message) : JSON.stringify(e));
      this.platformUtilsService.showToast("error", "", this.i18nService.t("unexpectedError"));
    }
  }

  /** Open the partner picker to manually link this transaction to another. On
      back, return to these details. */
  openLinkPicker(): void {
    this.dialogRef?.close();
    LinkTransactionPickerComponent.open(this.currentTransaction(), this.dialog, () => {
      this.dialog.closeAll();
      TransactionDetailsComponent.open(this.rowData(), this.dialog);
    });
  }

  static open(
    rowData: TableRow | TransactionDetailType,
    dialog: MatDialog,
    dialogRef?: MatDialogRef<DialogWrapperComponent>,
  ): void {
    dialogRef?.close();
    dialog.open(DialogWrapperComponent, {
      ...RIGHT_PANEL_CONFIG,
      disableClose: false,
      data: {
        title: "transactionDetails",
        showBackButton: false,
        component: TransactionDetailsComponent,
        componentInputs: { rowData },
      },
    });
  }

  openEditTransaction(tab?: string): void {
    if (!this.isManual()) {
      return;
    }
    this.dialogRef?.close();
    this.rightPanel.open(DialogWrapperComponent, {
      data: {
        title: "transactionDetails",
        showBackButton: true,
        component: EditTransactionDetailsComponent,
        componentInputs: { rowData: this.rowData(), tab: tab },
        onBackClicked: () => {
          this.dialog.closeAll();
          TransactionDetailsComponent.open(this.rowData(), this.dialog);
        },
      },
    });
  }

  goCategorySetting() {
    this.openEditTransaction(editTransactionTab.categories);
  }

  getData(rowData: TableRow | TransactionDetailType): TransactionData | StockTransactionData {
    const essentialTransData = {
      origin: getTransactionOrigin(rowData.transactionView, rowData.accountView),
      amount: rowData.transactionView?.quantity?.actualQuantity?.amount,
      date: rowData.transactionDate,
      description: rowData.description,
      direction: rowData.transactionView?.direction,
      source: `${rowData.institution} - ${rowData.account}`,
      symbol: rowData.transactionView?.quantity?.currency,
      account: rowData.accountView?.name,
      transactionSymbol: rowData.transactionView?.quantity.actualQuantity,
      accounts: rowData.accountView?.c_institution?.availableAccounts,
      categoriesIds: rowData.transactionView?.c_categories,
    };

    if (this.isStockOrOtherAccount()) {
      const unitPrice = rowData.transactionView.c_valuation.symbolValue;
      return {
        ...essentialTransData,
        unitPrice: unitPrice,
        totalValue: unitPrice * essentialTransData.amount,
        baseCurrencySymbol: rowData.transactionView.quantity.currency,
      } as StockTransactionData;
    } else {
      return essentialTransData as TransactionData;
    }
  }

  protected isStockTransactionData(
    data: TransactionData | StockTransactionData,
  ): data is StockTransactionData {
    return "unitPrice" in data && "totalValue" in data && "baseCurrencySymbol" in data;
  }
}
